import { z } from 'zod';

const RuleBaseSchema = {
    id: z.string().min(1),
    description: z.string().min(1),
    priority: z.number().int().positive(),
    accountId: z.string().min(1),
    splitPercentage: z.number().min(0).max(1),
    monthlyContributionMaxCents: z.number().int().nonnegative().nullish(),
    yearlyContributionMaxCents: z.number().int().nonnegative().nullish(),
};

const PercentageRuleSchema = z.object({
    ...RuleBaseSchema,
    allocationType: z.literal('PERCENTAGE'),
    percentage: z.number().min(0).max(1),
});

const MinimumBalanceRuleSchema = z.object({
    ...RuleBaseSchema,
    allocationType: z.literal('MINIMUM_BALANCE'),
    targetBalanceCents: z.number().int().nonnegative(),
});

const DebtPayDownRuleSchema = z.object({
    ...RuleBaseSchema,
    accountId: z.string().optional(),
    allocationType: z.literal('DEBT_PAY_DOWN'),
    accountIds: z.array(z.string().min(1)).min(1).nullish(),
    strategy: z.enum(['MINIMUM_ONLY', 'AVALANCHE']),
});

const RuleSchema = z.discriminatedUnion('allocationType', [
    PercentageRuleSchema,
    MinimumBalanceRuleSchema,
    DebtPayDownRuleSchema,
]);

const RuleSetSchema = z
    .object({
        id: z.string().min(1),
        name: z.string().min(1),
        priority: z.number().int().positive(),
        rules: z.array(RuleSchema).min(1),
    })
    .refine(
        (rs) => {
            const sum = rs.rules.reduce((s, r) => s + r.splitPercentage, 0);
            return Math.abs(sum - 1.0) < 0.001;
        },
        { message: 'splitPercentage values within a rule set must sum to 1.0' },
    );

const DepositoryAccountSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    type: z.enum(['CHECKING', 'SAVINGS', 'EXTERNAL']),
    balanceCents: z.number().int(),
});

const TaxableInvestmentAccountSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    type: z.literal('TAXABLE_INVESTMENT'),
    balanceCents: z.number().int().nonnegative(),
});

const IRAAccountSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    type: z.enum(['TRADITIONAL_IRA', 'ROTH_IRA']),
    balanceCents: z.number().int().nonnegative(),
    annualContributionLimitCents: z.number().int().nonnegative(),
    initialYtdContributionCents: z.number().int().nonnegative(),
});

const DebtAccountSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    type: z.literal('DEBT'),
    balanceCents: z.number().int().nonnegative(),
    annualInterestRate: z.number().min(0),
    minimumPaymentCents: z.number().int().nonnegative(),
});

const AccountSchema = z.discriminatedUnion('type', [
    DepositoryAccountSchema,
    TaxableInvestmentAccountSchema,
    IRAAccountSchema,
    DebtAccountSchema,
]);

export const MoneyMapSimulationSchema = z
    .object({
        monthlyGoalsCents: z.number().int().positive(),
        incomeAccountId: z.string().min(1),
        ruleSets: z.array(RuleSetSchema).min(1),
        accounts: z.array(AccountSchema).min(1),
    })
    .superRefine((data, ctx) => {
        const accountIds = new Set(data.accounts.map((a) => a.id));

        if (!accountIds.has(data.incomeAccountId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `incomeAccountId "${data.incomeAccountId}" does not match any account`,
                path: ['incomeAccountId'],
            });
        }

        for (const [si, ruleSet] of data.ruleSets.entries()) {
            for (const [ri, rule] of ruleSet.rules.entries()) {
                if (rule.allocationType === 'DEBT_PAY_DOWN') {
                    if (rule.strategy === 'AVALANCHE') {
                        if (rule.accountId != null && rule.accountId !== '') {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `AVALANCHE rule "${rule.id}" must not have accountId; use accountIds instead`,
                                path: [
                                    'ruleSets',
                                    si,
                                    'rules',
                                    ri,
                                    'accountId',
                                ],
                            });
                        }
                        if (
                            rule.accountIds == null ||
                            rule.accountIds.length === 0
                        ) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `AVALANCHE rule "${rule.id}" must have accountIds`,
                                path: [
                                    'ruleSets',
                                    si,
                                    'rules',
                                    ri,
                                    'accountIds',
                                ],
                            });
                        } else {
                            for (const accId of rule.accountIds) {
                                if (!accountIds.has(accId)) {
                                    ctx.addIssue({
                                        code: z.ZodIssueCode.custom,
                                        message: `AVALANCHE rule "${rule.id}" references unknown account "${accId}"`,
                                        path: [
                                            'ruleSets',
                                            si,
                                            'rules',
                                            ri,
                                            'accountIds',
                                        ],
                                    });
                                }
                                const account = data.accounts.find(
                                    (a) => a.id === accId,
                                );
                                if (account && account.type !== 'DEBT') {
                                    ctx.addIssue({
                                        code: z.ZodIssueCode.custom,
                                        message: `AVALANCHE rule "${rule.id}" targets non-debt account "${accId}"`,
                                        path: [
                                            'ruleSets',
                                            si,
                                            'rules',
                                            ri,
                                            'accountIds',
                                        ],
                                    });
                                }
                            }
                        }
                    } else {
                        if (
                            rule.accountIds != null &&
                            rule.accountIds.length > 0
                        ) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Non-AVALANCHE DEBT_PAY_DOWN rule "${rule.id}" must not have accountIds; use accountId instead`,
                                path: [
                                    'ruleSets',
                                    si,
                                    'rules',
                                    ri,
                                    'accountIds',
                                ],
                            });
                        }
                        if (
                            rule.accountId == null ||
                            rule.accountId === '' ||
                            !accountIds.has(rule.accountId)
                        ) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Rule "${rule.id}" references unknown account "${rule.accountId ?? ''}"`,
                                path: [
                                    'ruleSets',
                                    si,
                                    'rules',
                                    ri,
                                    'accountId',
                                ],
                            });
                        } else {
                            const account = data.accounts.find(
                                (a) => a.id === rule.accountId,
                            );
                            if (account && account.type !== 'DEBT') {
                                ctx.addIssue({
                                    code: z.ZodIssueCode.custom,
                                    message: `DEBT_PAY_DOWN rule "${rule.id}" targets non-debt account "${rule.accountId}"`,
                                    path: [
                                        'ruleSets',
                                        si,
                                        'rules',
                                        ri,
                                        'accountId',
                                    ],
                                });
                            }
                        }
                    }
                } else {
                    if (
                        rule.accountId == null ||
                        rule.accountId === '' ||
                        !accountIds.has(rule.accountId)
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Rule "${rule.id}" references unknown account "${rule.accountId ?? ''}"`,
                            path: ['ruleSets', si, 'rules', ri, 'accountId'],
                        });
                    }
                }
            }
        }

        const ruleSetPriorities = data.ruleSets.map((rs) => rs.priority);
        if (new Set(ruleSetPriorities).size !== ruleSetPriorities.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Rule set priorities must be unique',
                path: ['ruleSets'],
            });
        }

        if (new Set(data.accounts.map((a) => a.id)).size !== accountIds.size) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Account IDs must be unique',
                path: ['accounts'],
            });
        }
    });

export type ValidationResult =
    | { valid: true }
    | { valid: false; errors: string[] };

export function validateMoneyMap(json: string): ValidationResult {
    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch {
        return { valid: false, errors: ['Invalid JSON syntax'] };
    }

    const result = MoneyMapSimulationSchema.safeParse(parsed);
    if (result.success) {
        return { valid: true };
    }

    const errors = result.error.issues.map((issue) => {
        const path = issue.path.length > 0 ? issue.path.join('.') : '(root)';
        return `${path}: ${issue.message}`;
    });

    return { valid: false, errors };
}
